import ThemeToggle from "../theme/ThemeToggle";
import React from "react";
import { Phone, Instagram, MapPin, Mail } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import BrandLogo from "./BrandLogo";
import SocialLinksBar from "./SocialLinksBar";
import { US_STATES } from "./quote/usStates";
import { assemblyPages, assemblyText } from "../seo/assemblyPages";

const STATE_COVERAGE = {
  en: {
    heading: "States",
    availability: "Request a quote by ZIP; our team confirms service availability.",
  },
  pt: {
    heading: "Estados",
    availability: "Solicite um orçamento pelo ZIP; nossa equipe confirma a disponibilidade de atendimento.",
  },
  es: {
    heading: "Estados",
    availability: "Solicita una cotización por ZIP; nuestro equipo confirma la disponibilidad del servicio.",
  },
};

const STATE_NAMES = {
  pt: {
    AK: "Alasca", CA: "Califórnia", FL: "Flórida", GA: "Geórgia", HI: "Havaí",
    LA: "Luisiana", MS: "Mississípi", MO: "Missúri", NH: "Nova Hampshire",
    NJ: "Nova Jersey", NM: "Novo México", NY: "Nova York", NC: "Carolina do Norte",
    ND: "Dakota do Norte", PA: "Pensilvânia", SC: "Carolina do Sul",
    SD: "Dakota do Sul", VA: "Virgínia", WV: "Virgínia Ocidental",
  },
  es: {
    HI: "Hawái", LA: "Luisiana", MI: "Míchigan", MS: "Misisipi", MO: "Misuri",
    NH: "Nueva Hampshire", NJ: "Nueva Jersey", NM: "Nuevo México", NY: "Nueva York",
    NC: "Carolina del Norte", ND: "Dakota del Norte", OR: "Oregón",
    PA: "Pensilvania", SC: "Carolina del Sur", SD: "Dakota del Sur",
    WV: "Virginia Occidental",
  },
};

const PRIORITY_STATES = ["CA", "NV", "OR"];
const FOOTER_STATES = Object.fromEntries(Object.keys(STATE_COVERAGE).map((lang) => {
  const stateName = (code) => STATE_NAMES[lang]?.[code] || US_STATES[code];
  const remaining = Object.keys(US_STATES)
    .filter((code) => code !== "DC" && !PRIORITY_STATES.includes(code))
    .sort((a, b) => stateName(a).localeCompare(stateName(b), lang));
  return [lang, [...PRIORITY_STATES, ...remaining].map((code) => ({ code, name: stateName(code) }))];
}));

const Footer = () => {
  const { t, lang } = useLang();
  const stateCoverage = STATE_COVERAGE[lang] || STATE_COVERAGE.en;
  const states = FOOTER_STATES[lang] || FOOTER_STATES.en;
  const access = { en: ["Become a Pro", "Partners", "Work with Xavier Assembly"], pt: ["Seja Pro", "Parceiros", "Trabalhe com a Xavier Assembly"], es: ["Sé Pro", "Socios", "Trabaja con Xavier Assembly"] }[lang] || ["Become a Pro", "Partners", "Work with Xavier Assembly"];
  const contactLabel = { en: "Contact us", pt: "Fale Conosco", es: "Contáctanos" }[lang] || "Contact us";
  const proLogin = { en: "Pro Login", pt: "Login Pro", es: "Acceso Pro" }[lang] || "Pro Login";
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-black border-t border-white/5 pt-16 sm:pt-20 pb-8 safe-pb safe-px"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="mb-6 flex justify-end"><ThemeToggle testId="footer-theme-toggle" /></div>
        <nav aria-label={{en:'Installation services',pt:'Serviços de instalação',es:'Servicios de instalación'}[lang] || 'Installation services'} className="mb-10 flex flex-wrap gap-x-6 gap-y-4 text-sm text-sky-300">
          <a href="/gazebo-installer">{{en:'Gazebo installer',pt:'Instalação de gazebo',es:'Instalación de gazebo'}[lang] || 'Gazebo installer'}</a>
          <a href="/pergola-installer">{{en:'Pergola installer',pt:'Instalação de pergolado',es:'Instalación de pérgola'}[lang] || 'Pergola installer'}</a>
          <a href="/costco-gazebo-installer">Costco gazebo</a>
          <a href="/yardistry-installer">Yardistry</a>
          {Object.entries(assemblyPages).map(([href,page]) => <a className="hover:underline" key={href} href={href}>{assemblyText(page.label,lang)}</a>)}
        </nav>
        <nav aria-label={access[2]} className="mb-10 flex flex-col gap-3 rounded border border-sky-400/30 bg-sky-500/5 p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-display text-lg font-bold text-white">{access[2]}</p>
          <div className="flex flex-wrap gap-3">{[["/pro", access[0], "footer-pro"], ["/installer", proLogin, "footer-pro-login"], ["/partners", access[1], "footer-partners"]].map(([href, label, id]) => <a key={href} href={href} data-testid={id} className="inline-flex min-h-[48px] flex-1 items-center justify-center rounded border border-sky-400 px-6 py-3 font-bold text-sky-200 hover:bg-sky-500/15 focus:outline-none focus:ring-2 focus:ring-sky-300 sm:flex-none">{label}</a>)}</div>
        </nav>
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-x-6 gap-y-10 sm:gap-12 mb-12 sm:mb-16">
          <div className="col-span-2 lg:col-span-5 space-y-6">
            <div className="flex items-center gap-3">
              <BrandLogo size="h-16 w-16 sm:h-20 sm:w-20" />
              <div>
                <div className="font-display font-bold text-white text-base tracking-wide">
                  XAVIER ASSEMBLY
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-sky-400">
                  Outdoor & Gym Pro
                </div>
              </div>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-md">
              {t.footer.tagline}
            </p>
            <div className="flex items-center gap-4 pt-2 flex-wrap">
              <SocialLinksBar testId="footer-social-links" />
              <a
                data-testid="footer-phone-icon"
                href="tel:+15105079318"
                className="h-10 w-10 flex items-center justify-center border border-white/15 text-white hover:bg-sky-500 hover:border-sky-500 transition-colors"
                aria-label="Call"
              >
                <Phone size={16} />
              </a>
            </div>
          </div>

          <div className="col-span-1 lg:col-span-4 space-y-5">
            <div className="eyebrow">{t.footer.contact}</div>
            <div className="space-y-3 text-zinc-300 text-sm">
              <a href="/contact" data-testid="footer-contact" className="inline-flex min-h-[44px] items-center rounded text-sky-300 underline underline-offset-4 focus:outline-none focus:ring-2 focus:ring-sky-300">{contactLabel}</a>
              <a
                data-testid="footer-phone"
                href="tel:+15105079318"
                className="flex items-center gap-3 hover:text-sky-400 transition-colors"
              >
                <Phone size={14} className="text-sky-400" />
                (510) 507-9318
              </a>
              <a
                href="https://instagram.com/xavierassembler"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 hover:text-sky-400 transition-colors"
              >
                <Instagram size={14} className="text-sky-400" />
                @xavierassembler
              </a>
              <div className="flex items-center gap-3">
                <MapPin size={14} className="text-sky-400" />
                {t.footer.quote_area}
              </div>
              <div className="flex items-center gap-3">
                <Mail size={14} className="text-sky-400" />
                <a href="mailto:quotes@xavierassembly.com" className="hover:text-sky-400 transition-colors" data-testid="footer-email-link">
                  quotes@xavierassembly.com
                </a>
              </div>
            </div>
          </div>

          <div className="col-span-1 lg:col-span-3 space-y-5">
            <div className="eyebrow">{t.footer.languages}</div>
            <ul className="space-y-2 text-zinc-300 text-sm">
              <li>English</li>
              <li>Português</li>
              <li>Español</li>
            </ul>
          </div>


          <section data-testid="footer-states" aria-labelledby="footer-states-heading" className="col-span-2 lg:col-span-12 space-y-5 text-center">
            <h2 id="footer-states-heading" className="eyebrow">{stateCoverage.heading}</h2>
            <p className="mx-auto max-w-2xl text-zinc-400 text-sm leading-relaxed">{stateCoverage.availability}</p>
            <ul className="mx-auto grid max-w-5xl grid-cols-2 gap-x-4 gap-y-3 text-left text-sm leading-relaxed text-zinc-300 sm:grid-cols-3 lg:grid-cols-5">
              {states.map(({ code, name }) => <li key={code} data-state={code} className="min-w-0 break-words">{name}</li>)}
            </ul>
          </section>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-zinc-500 text-xs">
            © {year} Xavier Assembly. {t.footer.rights}
          </p>
          <p className="text-zinc-600 text-[11px] max-w-2xl text-right">
            {t.footer.keywords}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
